import { useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import go from "highlight.js/lib/languages/go";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import markdown from "highlight.js/lib/languages/markdown";
import python from "highlight.js/lib/languages/python";
import rust from "highlight.js/lib/languages/rust";
import sql from "highlight.js/lib/languages/sql";
import typescript from "highlight.js/lib/languages/typescript";
import "highlight.js/styles/github.css";

const REGISTERED = new Set<string>();
function ensureRegistered() {
  if (REGISTERED.size > 0) return;
  hljs.registerLanguage("bash", bash);
  hljs.registerLanguage("sh", bash);
  hljs.registerLanguage("shell", bash);
  hljs.registerLanguage("go", go);
  hljs.registerLanguage("javascript", javascript);
  hljs.registerLanguage("js", javascript);
  hljs.registerLanguage("json", json);
  hljs.registerLanguage("markdown", markdown);
  hljs.registerLanguage("md", markdown);
  hljs.registerLanguage("python", python);
  hljs.registerLanguage("py", python);
  hljs.registerLanguage("rust", rust);
  hljs.registerLanguage("rs", rust);
  hljs.registerLanguage("sql", sql);
  hljs.registerLanguage("typescript", typescript);
  hljs.registerLanguage("ts", typescript);
  hljs.registerLanguage("tsx", typescript);
  REGISTERED.add("done");
}

// Allow class="language-…" on <code> through the sanitizer so our highlight.js
// CSS keeps working. Everything else stays at the rehype defaults (no script/
// iframe/style; URL schemes limited to http(s)/mailto/etc).
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ["className", /^language-/]],
    span: [...(defaultSchema.attributes?.span ?? []), ["className", /^hljs/]],
  },
};

function HighlightedCode({
  className,
  children,
  ...rest
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  ensureRegistered();
  const langMatch = /language-([\w+-]+)/.exec(className || "");
  const isBlock = Boolean(langMatch);
  const code = String(children ?? "").replace(/\n$/, "");

  if (!isBlock) {
    return (
      <code
        className="rounded bg-zinc-100 dark:bg-zinc-800 px-1 py-0.5 text-[0.9em] font-mono"
        {...rest}
      >
        {children}
      </code>
    );
  }

  const lang = langMatch![1].toLowerCase();
  let html: string;
  try {
    html = hljs.getLanguage(lang)
      ? hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
      : escapeHtml(code);
  } catch {
    html = escapeHtml(code);
  }
  return (
    <code
      className={`hljs language-${lang}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Tailwind Preflight strips default UA styling (heading sizes, list bullets,
// paragraph spacing). The project doesn't load @tailwindcss/typography, so
// `prose` classes are no-ops — every element below needs explicit classes.
const MD_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-semibold tracking-tight mt-6 mb-3 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold tracking-tight mt-5 mb-2 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold mt-3 mb-1 first:mt-0">{children}</h4>
  ),
  p: ({ children }) => <p className="my-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-6 my-3 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 my-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-zinc-300 dark:border-zinc-700 pl-3 my-3 text-zinc-600 dark:text-zinc-400 italic">
      {children}
    </blockquote>
  ),
  hr: () => (
    <hr className="my-6 border-t border-zinc-200 dark:border-zinc-800" />
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: HighlightedCode,
  pre: ({ children }) => (
    <pre className="rounded-md bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-3 text-xs overflow-x-auto my-3">
      {children}
    </pre>
  ),
  a: ({ href, children, ...rest }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent hover:underline"
      {...rest}
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th className="border border-zinc-300 dark:border-zinc-700 px-2 py-1 text-left bg-zinc-50 dark:bg-zinc-900 font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-zinc-300 dark:border-zinc-700 px-2 py-1 align-top">
      {children}
    </td>
  ),
};

/**
 * Renders Markdown bodies: GFM (tables, task lists, autolinks, strikethrough),
 * fenced code blocks highlighted by highlight.js, and sanitized HTML output so
 * untrusted authors can't inject script/iframe/etc.
 *
 * Used for question + answer bodies (Q&A view) and regular doc section bodies.
 * The parent typically wraps with `prose dark:prose-invert` to inherit
 * Tailwind Typography spacing for headings/lists.
 */
export function CodeAwareBody({ body }: { body: string }) {
  const trimmed = body ?? "";
  const components = useMemo(() => MD_COMPONENTS, []);
  if (!trimmed) {
    return <span className="text-zinc-400 italic">No content yet.</span>;
  }
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
      components={components}
    >
      {trimmed}
    </ReactMarkdown>
  );
}

/** Back-compat export for the previous string-returning API. Not used by the
 *  SPA anymore (CodeAwareBody returns React directly), but kept so any
 *  downstream importer doesn't break. */
export function renderBodyWithCode(body: string): string {
  return body ?? "";
}
